import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { PageHeader, Badge, Btn, Table, Stat, inputCls } from "../../components/admin/AdminUI";
import { useAdmin, can } from "../../components/admin/AdminLayout";

const Logs = ({ type }) => {
  const [d, setD] = useState({ items: [], total: 0 });
  useEffect(() => { api.get("/admin/email/logs", { params: { type } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [type]);
  return <Table testid={`logs-${type}`} rows={d.items} empty="No emails logged" cols={[["to", "Recipient"], ["subject", "Subject / Template", (r) => r.subject || r.template_key || r.campaign_id?.slice(0, 8)],
    ["status", "Status", (r) => <Badge s={r.status} />], ["provider_message_id", "Provider message ID", (r) => <span className="font-mono text-[11px] text-[#7C8A7C]">{r.provider_message_id || "—"}</span>],
    ["error", "Error", (r) => <span className="text-red-300 text-xs whitespace-normal">{r.error || ""}</span>], ["at", "Time", (r) => fmtDateTime(r.updated_at || r.created_at)]]} />;
};

const Suppression = () => {
  const admin = useAdmin();
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const load = () => api.get("/admin/email/suppression").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => { try { await api.post("/admin/email/suppression", { email, reason: "manual" }); setEmail(""); toast.success("Address suppressed"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const rm = async (id) => { try { await api.delete(`/admin/email/suppression/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (<>
    {can(admin, "manage_campaigns") && <div className="flex gap-2 mb-4"><Input placeholder="email@domain.com" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} w-[280px]`} data-testid="suppress-email-input" /><Btn onClick={add} data-testid="suppress-add-btn">Suppress Address</Btn></div>}
    <Table testid="suppression-table" rows={items} empty="Suppression list is empty" cols={[["email", "Email"], ["reason", "Reason", (r) => <Badge s={r.reason} />], ["source", "Source"], ["created_at", "Added", (r) => fmtDateTime(r.created_at)],
      ["x", "", (r) => r.reason !== "unsubscribed" && can(admin, "manage_settings") ? <Btn variant="ghost" onClick={() => rm(r.id)}>Remove</Btn> : null]]} />
  </>);
};

const Overview = () => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/email/overview").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const tx = (s) => d.transactional.filter((x) => x.type === "transactional" && x.status === s).reduce((a, b) => a + b.count, 0);
  return (<div className="space-y-5" data-testid="email-overview">
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
      <Stat label="Transactional accepted" value={tx("accepted")} accent /><Stat label="Transactional failed" value={tx("failed")} />
      <Stat label="Campaign accepted" value={d.campaign_recipients.accepted || 0} /><Stat label="Campaign failed/bounced" value={(d.campaign_recipients.failed || 0) + (d.campaign_recipients.bounced || 0)} />
      <Stat label="Suppressed" value={d.suppressed} /><Stat label="Templates" value={d.templates} />
    </div>
    <p className="text-xs text-[#7C8A7C]">"Accepted" means the SMTP server accepted the message. Hostinger SMTP does not provide delivery receipts, so final inbox delivery cannot be confirmed; immediate rejections are recorded as failed or bounced.</p>
    <p className="eyebrow">Scheduled emails</p>
    <Table testid="scheduled-table" rows={d.scheduled} empty="Nothing scheduled" cols={[["name", "Campaign"], ["recipient_count", "Recipients"], ["scheduled_at", "Scheduled for", (r) => fmtDateTime(r.scheduled_at)], ["approved_by", "Approved by"]]} />
  </div>);
};

export default function AdminDeliveryLogs() {
  return (
    <div data-testid="admin-delivery-logs">
      <PageHeader title="Email Delivery" subtitle="Overview, delivery logs, scheduled emails and suppression list" />
      <Tabs defaultValue="overview">
        <TabsList className="bg-[#111511] border border-[#1E281E] mb-5 flex-wrap h-auto">
          {[["overview", "Overview"], ["transactional", "Transactional"], ["campaign", "Campaign"], ["test", "Test Sends"], ["suppression", "Suppression List"]].map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`tab-${k}`} className="data-[state=active]:bg-[#B7FF3C] data-[state=active]:text-[#080A08]">{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="overview"><Overview /></TabsContent>
        <TabsContent value="transactional"><Logs type="transactional" /></TabsContent>
        <TabsContent value="campaign"><Logs type="campaign" /></TabsContent>
        <TabsContent value="test"><Logs type="test" /></TabsContent>
        <TabsContent value="suppression"><Suppression /></TabsContent>
      </Tabs>
    </div>
  );
}
